import React, { useState } from 'react';
import { Gift, Send, DollarSign, User, Mail, Phone, MessageSquare, Check, X, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { giftcardApi } from '../../../services/giftcardApi';

const FIXED_AMOUNTS = [25, 50, 100, 200];

export default function GiftCardSellModal({ open, onClose, locationId, cashierId }) {
  const [step, setStep] = useState('amount'); // amount | details | confirm | success
  const [amount, setAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [form, setForm] = useState({
    recipient_name: '', recipient_email: '', recipient_phone: '',
    purchaser_name: '', purchaser_phone: '', sender_message: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const finalAmount = amount === 'custom' ? parseFloat(customAmount) : amount;
  const hasContact = form.recipient_email || form.recipient_phone;
  const canProceed = finalAmount > 0 && form.recipient_name && hasContact;

  const handleSell = async () => {
    setLoading(true);
    try {
      const res = await giftcardApi.sell({
        amount: finalAmount,
        recipient_name: form.recipient_name,
        recipient_email: form.recipient_email || undefined,
        recipient_phone: form.recipient_phone || undefined,
        purchaser_name: form.purchaser_name || undefined,
        purchaser_phone: form.purchaser_phone || undefined,
        sender_name: form.purchaser_name || undefined,
        sender_message: form.sender_message || undefined,
        delivery_method: form.recipient_email ? 'email' : 'sms',
        send_now: true,
        payment_confirmed: true,
        source_channel: 'restaurant_pos',
        location_id: locationId,
        created_by_user_id: cashierId,
        idempotency_key: `pos-sell-${Date.now()}`,
      });
      setResult(res);
      setStep('success');
      toast.success(`Gift card sold: $${finalAmount}`);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  const reset = () => {
    setStep('amount'); setAmount(null); setCustomAmount('');
    setForm({ recipient_name: '', recipient_email: '', recipient_phone: '', purchaser_name: '', purchaser_phone: '', sender_message: '' });
    setResult(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="gc-sell-modal">
      <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" />
            <span className="text-white font-semibold">Sell Gift Card</span>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="text-gray-500 hover:text-white" data-testid="gc-sell-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4">
          {/* Step 1: Amount */}
          {step === 'amount' && (
            <div data-testid="gc-sell-step-amount">
              <p className="text-gray-400 text-sm mb-4">Select amount</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {FIXED_AMOUNTS.map(a => (
                  <button key={a} onClick={() => { setAmount(a); setStep('details'); }}
                    className={`p-4 rounded-xl border text-center text-xl font-bold transition ${amount === a ? 'border-amber-400 bg-amber-400/10 text-amber-400' : 'border-white/10 text-white hover:border-white/30'}`}
                    data-testid={`gc-amount-${a}`}>
                    ${a}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <DollarSign className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                  <input type="number" placeholder="Custom" value={customAmount} onChange={e => { setCustomAmount(e.target.value); setAmount('custom'); }}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white placeholder-gray-500 focus:border-amber-400/50 outline-none"
                    data-testid="gc-custom-amount" />
                </div>
                <Button onClick={() => setStep('details')} disabled={amount !== 'custom' || !customAmount || parseFloat(customAmount) <= 0}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6" data-testid="gc-custom-next">Next</Button>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 'details' && (
            <div data-testid="gc-sell-step-details">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-400 text-sm">Gift card for <span className="text-amber-400 font-bold">${finalAmount}</span></p>
                <button onClick={() => setStep('amount')} className="text-xs text-gray-500 hover:text-white">Change</button>
              </div>
              <div className="space-y-3">
                <p className="text-gray-500 text-xs uppercase tracking-wider">Recipient</p>
                <div className="relative">
                  <User className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                  <input placeholder="Recipient Name *" value={form.recipient_name} onChange={e => setForm({ ...form, recipient_name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-amber-400/50"
                    data-testid="gc-recipient-name" />
                </div>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                  <input type="email" placeholder="Recipient Email" value={form.recipient_email} onChange={e => setForm({ ...form, recipient_email: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-amber-400/50"
                    data-testid="gc-recipient-email" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                  <input placeholder="Recipient Phone" value={form.recipient_phone} onChange={e => setForm({ ...form, recipient_phone: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-amber-400/50"
                    data-testid="gc-recipient-phone" />
                </div>
                {!form.recipient_email && !form.recipient_phone && (
                  <p className="text-red-400 text-xs">Email or phone required for delivery</p>
                )}
                <p className="text-gray-500 text-xs uppercase tracking-wider pt-2">Purchaser</p>
                <div className="relative">
                  <User className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                  <input placeholder="Purchaser Name" value={form.purchaser_name} onChange={e => setForm({ ...form, purchaser_name: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-amber-400/50"
                    data-testid="gc-purchaser-name" />
                </div>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                  <input placeholder="Purchaser Phone (for confirmation SMS)" value={form.purchaser_phone} onChange={e => setForm({ ...form, purchaser_phone: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-amber-400/50"
                    data-testid="gc-purchaser-phone" />
                </div>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                  <textarea placeholder="Gift message (optional)" value={form.sender_message} onChange={e => setForm({ ...form, sender_message: e.target.value })} rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-amber-400/50 resize-none" />
                </div>
              </div>
              <Button onClick={() => setStep('confirm')} disabled={!canProceed}
                className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold h-12" data-testid="gc-sell-review">
                Review & Sell
              </Button>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <div data-testid="gc-sell-step-confirm">
              <div className="bg-white/5 rounded-xl p-4 mb-4 space-y-2">
                <div className="flex justify-between"><span className="text-gray-400">Amount</span><span className="text-amber-400 font-bold text-xl">${finalAmount}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">To</span><span className="text-white">{form.recipient_name}</span></div>
                {form.recipient_email && <div className="flex justify-between"><span className="text-gray-400">Email</span><span className="text-white text-sm">{form.recipient_email}</span></div>}
                {form.recipient_phone && <div className="flex justify-between"><span className="text-gray-400">Recipient Phone</span><span className="text-white text-sm">{form.recipient_phone}</span></div>}
                {form.purchaser_name && <div className="flex justify-between"><span className="text-gray-400">Purchaser</span><span className="text-white">{form.purchaser_name}</span></div>}
                {form.purchaser_phone && <div className="flex justify-between"><span className="text-gray-400">Purchaser Phone</span><span className="text-white text-sm">{form.purchaser_phone}</span></div>}
                <div className="flex justify-between"><span className="text-gray-400">Delivery</span><span className="text-white">{form.recipient_email ? 'Email' : 'SMS'} (Immediate)</span></div>
                {form.purchaser_phone && <p className="text-cyan-400 text-xs pt-1">Purchaser will receive a confirmation SMS</p>}
                {form.recipient_phone && <p className="text-cyan-400 text-xs">Recipient will receive a gift card SMS</p>}
              </div>
              <div className="flex gap-3">
                <Button onClick={() => setStep('details')} variant="outline" className="flex-1 border-white/10 text-white hover:bg-white/5">Back</Button>
                <Button onClick={handleSell} disabled={loading}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold h-12" data-testid="gc-sell-confirm-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Sell Gift Card</>}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && result && (
            <div className="text-center py-4" data-testid="gc-sell-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">Gift Card Sold!</h3>
              <p className="text-amber-400 text-2xl font-bold mb-3">${result.original_amount}</p>
              <div className="bg-white/5 rounded-xl p-3 mb-4 text-left space-y-1">
                <div className="flex justify-between text-sm"><span className="text-gray-400">Code</span><span className="text-white font-mono">{result.code}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">Delivery</span><span className="text-emerald-400">{result.delivery_status === 'sent' ? 'Sent' : 'Pending'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">Status</span><span className="text-white capitalize">{result.status}</span></div>
              </div>
              <Button onClick={() => { reset(); onClose(); }} className="w-full bg-white/10 hover:bg-white/20 text-white" data-testid="gc-sell-done">Done</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
